/**
 * Equity Card Formatter
 *
 * Template-based equity card system for per-class shareholder card rendering.
 * Reuses HeadlineFormatter's template processing infrastructure.
 *
 * =============================================================================
 * TEMPLATE SYNTAX (Same as HeadlineFormatter)
 * =============================================================================
 *
 * 1. SIMPLE VARIABLE INTERPOLATION
 *    {{variableName}}
 *    Example: "{{youownLabel}}: {{owned}} {{sharesType}}" → "You Own: 1,000 shares"
 *
 * 2. EXISTENCE CHECK - Show content only if variable exists and is non-zero
 *    {{#if variableName}}content{{/if}}
 *    Example: "{{#if hasVesting}}Your plan{{else}}You Own{{/if}}"
 *
 * 3. THRESHOLD CONDITIONAL - Compare variable to a number
 *    {{#if variableName > threshold}}trueValue{{else}}falseValue{{/if}}
 *    Operators: >, >=, <, <=, ==, !=
 *
 * 4. NUMBER FORMATTING - Control decimal places for currency values
 *    {{variableName | dollars}}  - Rounded to whole dollars
 *    {{variableName | cents}}    - Show dollars and cents (2 decimal places)
 *
 * =============================================================================
 * AVAILABLE VARIABLES
 * =============================================================================
 *
 * User share data:
 *   owned          - Total shares owned
 *   approved       - Vested/approved shares
 *   committed      - Committed capital
 *   tranches       - Number of tranches
 *   expDate        - Expiration date
 *   orig           - Original grant (bad actors)
 *
 * Client equity config:
 *   name           - Class display name
 *   sharesType     - "shares", "units", "RSUs"
 *   youownLabel    - Custom holdings label
 *   costbasisLabel - Custom cost basis label
 *
 * Calculated values:
 *   basis          - Cost basis
 *   value          - Current value
 *   interest       - Accrued interest
 *   uncalled       - Approved - owned (or committed)
 *
 * Calculator values (when available from PrivateCompany409A or other calculators):
 *   vested         - Vested share count
 *   unvested       - Unvested share count (owned - vested)
 *   vestedValue    - Dollar value of vested shares
 *   unvestedValue  - Dollar value of unvested shares
 *   sharePrice     - Current share price
 *   strikePrice    - Strike price for options
 *
 * Derived values:
 *   hasVesting     - Boolean: user has vesting schedule
 *   hasCalculator  - Boolean: calculator data is available
 *   isDebt         - Boolean: has interestRate
 *   vestedPercent  - vested / owned * 100
 *   uncalledLabel  - "Uncalled Capital" or "Unvested Equity"
 *   tradeAction    - "sell", "purchase", or "trade"
 *
 * =============================================================================
 */

import { HeadlineFormatter } from './HeadlineFormatter.js';

const EquityCardFormatter = {

  // ========================================
  // VARIABLE TYPE DEFINITIONS
  // ========================================
  // Determines how each variable is formatted

  variableTypes: {
    // Share counts - formatted as plain numbers with commas
    owned: 'number',
    approved: 'number',
    vested: 'number',        // Vested share count (from calculator)
    unvested: 'number',      // Unvested share count (owned - vested)
    orig: 'number',
    tranches: 'number',

    // Currency values
    committed: 'currency',
    basis: 'currency',
    value: 'currency',
    interest: 'currency',
    uncalled: 'currency',
    vestedValue: 'currency',    // Value of vested shares (from calculator)
    unvestedValue: 'currency',  // Value of unvested shares (from calculator)
    sharePrice: 'currency',     // Current share price (from calculator)
    strikePrice: 'currency',    // Strike price for options (from calculator)

    // Income allocations (tax basis + market value)
    taxBasis: 'currency',
    incomeAllocations: 'currency',
    contributions: 'currency',
    distributions: 'currency',
    marketValue: 'currency',
    currentValue: 'currency',
    unrealizedGain: 'currency',

    // Percentage values
    vestedPercent: 'percentage',

    // Text/label variables (raw, will be HTML-escaped)
    name: 'raw',
    sharesType: 'raw',
    youownLabel: 'raw',
    costbasisLabel: 'raw',
    uncalledLabel: 'raw',
    tradeAction: 'raw',
    expDate: 'raw',

    // Boolean flags (used in conditionals, not for display)
    hasVesting: 'raw',
    isDebt: 'raw',
    hasCalculator: 'raw'
  },

  // ========================================
  // DEFAULT TEMPLATES
  // ========================================
  // Used when no custom template is configured
  // Lines are numbered for simplicity - content is defined by template

  defaultTemplates: {
    line1: '{{youownLabel}}: {{owned}} {{sharesType}}',
    line2: '',
    line3: '',
    line4: ''
  },

  /**
   * Format a single value based on its type
   * @param {*} value - The value to format
   * @param {string} type - 'currency', 'number', 'percentage', or 'raw'
   * @param {string} [formatOverride] - Optional format override ('dollars', 'cents')
   * @returns {string} - Formatted string (HTML-safe)
   */
  formatValue: function(value, type, formatOverride) {
    if (value === undefined || value === null) return '';

    switch (type) {
      case 'currency':
        if (typeof value !== 'number' || isNaN(value)) return '';
        // Check for format override
        if (formatOverride === 'cents') {
          return '$' + value.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        }
        // Default: rounded dollars
        return '$' + value.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });

      case 'number':
        if (typeof value !== 'number' || isNaN(value)) return '';
        return value.toLocaleString('en-US');

      case 'percentage':
        if (typeof value !== 'number' || isNaN(value)) return '';
        return value.toFixed(2) + '%';

      default:
        // Escape HTML for raw string values to prevent XSS
        return HeadlineFormatter.escapeHtml(String(value));
    }
  },

  /**
   * Safely get a variable value, protecting against prototype pollution
   * @param {Object} vars - Variable map
   * @param {string} varName - Variable name to retrieve
   * @returns {*} - Variable value or undefined
   */
  safeGetVar: function(vars, varName) {
    return HeadlineFormatter.safeGetVar(vars, varName);
  },

  /**
   * Build a variable map from user share data, client equity config, and calculator results
   * @param {Object} options
   * @param {Object} options.userShares - User's share data for this class (uD[key])
   * @param {Object} options.equityConfig - Client equity config for this class (cD.equity[key])
   * @param {number} options.bases - Cost basis value
   * @param {number} options.values - Current value
   * @param {number} options.interest - Accrued interest (for ACC clients)
   * @param {Object} options.calculatorData - Per-class calculator breakdown (from window.lastCalculationResult.breakdown)
   * @returns {Object} - Flat map of variable names to values
   */
  buildVariableMap: function(options) {
    const { userShares = {}, equityConfig = {}, bases = 0, values = 0, interest = 0, calculatorData = null } = options;

    const owned = Number(userShares.owned || 0);
    const approved = Number(userShares.approved || 0);
    const committed = Number(userShares.committed || 0);

    // Calculate uncalled: use committed if set, otherwise approved - owned
    let uncalled = committed > 0 ? committed : Math.max(0, approved - owned);

    // Get calculator values if available
    const hasCalculator = !!calculatorData;
    const vested = hasCalculator ? Number(calculatorData.vested || 0) : approved;
    const unvested = owned - vested;

    const vars = {
      // User share data
      owned,
      approved,
      committed,
      tranches: userShares.tranches || 0,
      expDate: userShares.expDate || '',
      orig: Number(userShares.orig || 0),

      // Vesting counts (from calculator or fallback to approved)
      vested,
      unvested: Math.max(0, unvested),

      // Client equity config labels
      name: equityConfig.name || '',
      sharesType: equityConfig.sharesType || 'shares',
      youownLabel: equityConfig.youownLabel || (userShares.vesting ? 'Your plan' : 'You Own'),
      costbasisLabel: equityConfig.costbasisLabel || 'Cost Basis',

      // Calculated values
      basis: bases,
      value: values,
      interest: interest,
      uncalled: uncalled,

      // Derived boolean/computed values
      hasVesting: !!userShares.vesting,
      hasCalculator: hasCalculator,
      isDebt: !!equityConfig.interestRate,
      vestedPercent: owned > 0 ? (vested / owned) * 100 : 0,
      uncalledLabel: equityConfig.uncalledLabel || 'Uncalled',
      tradeAction: equityConfig.tradeAction || 'trade'
    };

    // Add calculator values if available
    if (hasCalculator) {
      if (calculatorData.vestedValue !== undefined) vars.vestedValue = calculatorData.vestedValue;
      if (calculatorData.unvestedValue !== undefined) vars.unvestedValue = calculatorData.unvestedValue;
      if (calculatorData.sharePrice !== undefined) vars.sharePrice = calculatorData.sharePrice;
      if (calculatorData.strikePrice !== undefined) vars.strikePrice = calculatorData.strikePrice;
      if (calculatorData.value !== undefined) vars.value = calculatorData.value;

      // Income allocations and tax basis values
      if (calculatorData.taxBasis !== undefined) vars.taxBasis = calculatorData.taxBasis;
      if (calculatorData.incomeAllocations !== undefined) vars.incomeAllocations = calculatorData.incomeAllocations;
      if (calculatorData.contributions !== undefined) vars.contributions = calculatorData.contributions;
      if (calculatorData.distributions !== undefined) vars.distributions = calculatorData.distributions;
      if (calculatorData.marketValue !== undefined) vars.marketValue = calculatorData.marketValue;
      if (calculatorData.currentValue !== undefined && vars.marketValue === undefined) {
        vars.marketValue = calculatorData.currentValue;
      }
      if (calculatorData.unrealizedGain !== undefined) vars.unrealizedGain = calculatorData.unrealizedGain;
    }

    return vars;
  },

  /**
   * Format a template string with variables
   * Delegates to HeadlineFormatter's formatWithVars for consistent template processing
   * @param {string} template - Template string with {{variables}}
   * @param {Object} vars - Variable map
   * @returns {string} - Formatted string
   */
  formatWithVars: function(template, vars) {
    if (!template) return '';

    // Step 1: Process conditionals ({{#if var}}...{{/if}}, {{var | default: "val"}})
    template = HeadlineFormatter.processConditionals(template, vars);

    // Step 2: Process format modifiers {{varName | dollars}} or {{varName | cents}}
    const self = this;
    template = template.replace(/\{\{(\w+)\s*\|\s*(dollars|cents)\}\}/g, function(match, varName, formatModifier) {
      const value = self.safeGetVar(vars, varName);
      const type = self.variableTypes[varName] || 'raw';
      return self.formatValue(value, type, formatModifier);
    });

    // Step 3: Process simple variable interpolation {{varName}}
    return template.replace(/\{\{(\w+)\}\}/g, function(match, varName) {
      const value = self.safeGetVar(vars, varName);
      const type = self.variableTypes[varName] || 'raw';
      return self.formatValue(value, type);
    });
  },

  /**
   * Get template with fallback chain:
   * 1. Per-class template (classConfig.line[N]_template)
   * 2. Built-in default (this.defaultTemplates.line[N])
   *
   * @param {string} lineKey - Line key ('line1', 'line2', 'line3', 'line4')
   * @param {Object} classConfig - Per-class equity config (cD.equity[key])
   * @returns {string} - Template to use
   */
  getTemplate: function(lineKey, classConfig) {
    // Check per-class override first
    const classTemplate = classConfig?.[`${lineKey}_template`];
    if (classTemplate) return classTemplate;

    // Fall back to built-in default
    return this.defaultTemplates[lineKey] || '';
  },

  /**
   * Render a single template line
   * @param {string} lineKey - Line key ('line1', 'line2', 'line3', 'line4')
   * @param {Object} classConfig - Per-class equity config
   * @param {Object} vars - Variable map from buildVariableMap
   * @returns {string} - Rendered line (empty string if template is empty)
   */
  renderLine: function(lineKey, classConfig, vars) {
    const template = this.getTemplate(lineKey, classConfig);
    if (!template) return '';
    return this.formatWithVars(template, vars);
  }
};


export { EquityCardFormatter };
